import React, { ReactElement, useState } from "react";
import { EventFilter } from "../Utilities/EventsApiService";
import { Calendar, CalendarProps, DatePicker } from "@progress/kendo-react-dateinputs";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../messages/en-US";
import moment from "moment-timezone";
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from "@progress/kendo-react-layout";
import PointsEventsPage from "./PointsEventsPage";
import EndpointsEventsPage from "./EndpointsEventsPage";
import { GetEndpointIcon, GetPointIcon } from "../../../Utilities/CustomIcons";
import { SvgIcon } from '@progress/kendo-react-common';
import * as svgIcons from '@progress/kendo-svg-icons';
import FlowRateEventsPage from "./FlowRateEventsPage";
import GpsEventsPage from "./GpsEventsPage";
import TemperatureEventsPage from "./TemperatureEventsPage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTemperatureHalf } from "@fortawesome/free-solid-svg-icons";
import { useAccountSettingsStore } from "../../../Zustand/accountSettingsStore";
import LoadingSpinner from "../../Shared/Components/LoadingSpinner";
import ErrorCard from "../../Shared/Components/Cards/ErrorCard";
import useIdentityStore from "../../../Zustand/identityStore";
 
// Disable Fast Navigational Bar until this critical bug is fixed by Telerik (https://github.com/telerik/kendo-react/issues/1566)
const CustomCalendar = (props: CalendarProps) => {
  return <Calendar {...props} navigation={false} />
};
 
export default function EventsPage(): ReactElement {
 
  const localizationService = useLocalization();
  const softwareFeatures = useAccountSettingsStore((state) => state.softwareFeatures);
  const timezone = useAccountSettingsStore((state) => state.accountTimezone);
  const user = useIdentityStore((store) => store.user);
 
  // Update initial state with timezone
  const [eventFilter, setEventFilter] = useState<EventFilter>({
    endDate: (() => {
      const endDate = moment().format("YYYY-MM-DDT23:59:59");
      return moment.tz(endDate, timezone).toDate();
    })(),
    startDate: (() => {
      const startDate = moment().subtract(30, 'days').format("YYYY-MM-DDT00:00:00");
      return moment.tz(startDate, timezone).toDate();
    })()
  });
 
  // Update date change handlers
  const handleStartDateChange = (date: any) => {
    const formattedDate = moment(date.target.value)
      .tz(timezone)
      .format("YYYY-MM-DDT00:00:00");
    const tzDate = moment.tz(formattedDate, timezone).toDate();
    setEventFilter(prev => ({ ...prev, startDate: tzDate }));
  };
 
  const handleEndDateChange = (date: any) => {
    const formattedDate = moment(date.target.value)
      .tz(timezone)
      .format("YYYY-MM-DDT23:59:59");
    const tzDate = moment.tz(formattedDate, timezone).toDate();
    setEventFilter(prev => ({ ...prev, endDate: tzDate }));
  };
 
  //TabStrip
  const [selected, setSelected] = React.useState<number>(0);
 
  const handleSelect = (e: TabStripSelectEventArguments) => {
    setSelected(e.selected);
  };
 
  const PointsTitle = () => { return(
    <div className="flex gap-2 items-center">
      {GetPointIcon(15,15)}
      <div>{localizationService.toLanguageString('custom.points', enMessages.custom.points)}</div>
    </div>)}
  
  const EndpointsTitle = () => { return(
    <div className="flex gap-2 items-center">
      {GetEndpointIcon(19,19)}
      <div>{localizationService.toLanguageString('custom.endpoints', enMessages.custom.endpoints)}</div>
    </div>)}
 
const FlowRateTitle = () => { return(
  <div className="flex gap-1 items-center">
    <SvgIcon icon={svgIcons.dropletIcon} style={{width: "20px"}} />
    <div>{localizationService.toLanguageString('custom.flowRate', enMessages.custom.flowRate)}</div>
  </div>)}
 
const GpsTitle = () => { return(
  <div className="flex gap-1 items-center">
    <SvgIcon icon={svgIcons.mapMarkerIcon} style={{width: "20px"}} />
    <div>{localizationService.toLanguageString('custom.gps', enMessages.custom.gps)}</div>
  </div>)}
 
const TemperatureTitle = () => { return(
  <div className="flex gap-1 items-center">
    <FontAwesomeIcon icon={faTemperatureHalf} size={"xl"}/>
    <div>{localizationService.toLanguageString('custom.temperature', enMessages.custom.temperature)}</div>
  </div>)}
 
  // Show loading if user is logged in but settings haven't been loaded yet
  if (user && softwareFeatures === null) {
    return <LoadingSpinner />;
  }

  if (!softwareFeatures) {
    return <ErrorCard />;
  }

  return (
    <div className="m-5">
      {/* Header */}
      <h2 className="gb-h2">
        {localizationService.toLanguageString('custom.events', enMessages.custom.events)}
        {timezone && <span className="ml-2 text-base font-normal text-slate-500">({timezone})</span>}
      </h2>
      <div className="gb-text-normal">{localizationService.toLanguageString('custom.eventsText', enMessages.custom.eventsText)}</div>
 
      {/* Date Picker for returned range */}
      <div className="flex flex-wrap my-5">
        <div>
          <div className="gb-text-normal font-bold">
            {localizationService.toLanguageString('custom.showEventsFrom', enMessages.custom.showEventsFrom)}
          </div>
          <DatePicker
            calendar={CustomCalendar}
            max={eventFilter.endDate!}
            onChange={handleStartDateChange}
            value={eventFilter.startDate}
            className="w-72"
          />
        </div>
        <div className="ml-4">
          <div className="gb-text-normal font-bold">
            {localizationService.toLanguageString('custom.until', enMessages.custom.until)}
          </div>
          <DatePicker
            calendar={CustomCalendar}
            min={eventFilter.startDate!}
            onChange={handleEndDateChange}
            value={eventFilter.endDate}
            className="w-72"
          />
        </div>
      </div>
 
      {/* Events Table */}
      <div className="mt-4" >
        <TabStrip selected={selected} onSelect={handleSelect}>
          {softwareFeatures.isManualSystemEnabled &&
            <TabStripTab title={PointsTitle()}>
              <PointsEventsPage eventFilter={eventFilter} timezone={timezone} />
            </TabStripTab>
          }
          {softwareFeatures.isAutomaticSystemEnabled &&
            <TabStripTab title={EndpointsTitle()}>
              <EndpointsEventsPage eventFilter={eventFilter} timezone={timezone} />
            </TabStripTab>
          }
          {softwareFeatures.isFlowRateSystemEnabled &&
            <TabStripTab title={FlowRateTitle()}>
              <FlowRateEventsPage eventFilter={eventFilter} />
            </TabStripTab>
          }
          {softwareFeatures.isFlowRateSystemEnabled &&
            <TabStripTab title={GpsTitle()}>
              <GpsEventsPage eventFilter={eventFilter} />
            </TabStripTab>
          }
           {softwareFeatures.isFlowRateSystemEnabled &&
            <TabStripTab title={TemperatureTitle()}>
              <TemperatureEventsPage eventFilter={eventFilter} />
            </TabStripTab>
          }
        </TabStrip>
        
        </div>
      </div>
  );
}